export const WORKPLACE_SETTINGS_KEY = "planOnline.settings.workplace";
export const WORKPLACE_SETTINGS_EVENT = "planOnline:workplace-settings-changed";

const LEGACY_DEFAULT_TABLE_EVEN_COLOR = "#cddded";
const LEGACY_DEFAULT_TABLE_SELECTED_COLOR = "#146c9c";

export const DEFAULT_WORKPLACE_SETTINGS = Object.freeze({
  tableEvenColor: "#f2f6f8",
  tableOddColor: "#ffffff",
  tableSelectedColor: "#c5e4f3",
  tableFontName: "",
  tableFontSize: 0,
  idTerm: 0,
  idSkl: 0,
  idBranch: 0
});

export const DEFAULT_TABLE_APPEARANCE = Object.freeze({
  tableEvenColor: DEFAULT_WORKPLACE_SETTINGS.tableEvenColor,
  tableOddColor: DEFAULT_WORKPLACE_SETTINGS.tableOddColor,
  tableSelectedColor: DEFAULT_WORKPLACE_SETTINGS.tableSelectedColor,
  tableFontName: DEFAULT_WORKPLACE_SETTINGS.tableFontName,
  tableFontSize: DEFAULT_WORKPLACE_SETTINGS.tableFontSize
});

const COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
let listenersInstalled = false;

const normalizeColor = (value, fallback) =>
  typeof value === "string" && COLOR_PATTERN.test(value) ? value.toLowerCase() : fallback;

const normalizeId = (value) => {
  const number = Number(value);
  return Number.isInteger(number) && number >= 0 ? number : 0;
};

export function normalizeWorkplaceSettings(value = {}) {
  return {
    tableEvenColor: normalizeColor(value.tableEvenColor, DEFAULT_WORKPLACE_SETTINGS.tableEvenColor),
    tableOddColor: normalizeColor(value.tableOddColor, DEFAULT_WORKPLACE_SETTINGS.tableOddColor),
    tableSelectedColor: normalizeColor(
      value.tableSelectedColor,
      DEFAULT_WORKPLACE_SETTINGS.tableSelectedColor
    ),
    tableFontName: typeof value.tableFontName === "string" ? value.tableFontName.trim() : "",
    tableFontSize: Math.max(0, Number(value.tableFontSize) || 0),
    idTerm: normalizeId(value.idTerm),
    idSkl: normalizeId(value.idSkl),
    idBranch: normalizeId(value.idBranch)
  };
}

export function loadWorkplaceSettings() {
  try {
    const stored = window.localStorage.getItem(WORKPLACE_SETTINGS_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    if (parsed?.tableEvenColor?.toLowerCase() === LEGACY_DEFAULT_TABLE_EVEN_COLOR) {
      parsed.tableEvenColor = DEFAULT_WORKPLACE_SETTINGS.tableEvenColor;
    }
    if (parsed?.tableSelectedColor?.toLowerCase() === LEGACY_DEFAULT_TABLE_SELECTED_COLOR) {
      parsed.tableSelectedColor = DEFAULT_WORKPLACE_SETTINGS.tableSelectedColor;
    }
    return normalizeWorkplaceSettings(parsed);
  } catch {
    return { ...DEFAULT_WORKPLACE_SETTINGS };
  }
}

export function applyWorkplaceSettings(value = loadWorkplaceSettings()) {
  const settings = normalizeWorkplaceSettings(value);
  const root = document.documentElement;

  root.style.setProperty("--plan-table-even-bg", settings.tableEvenColor);
  root.style.setProperty("--plan-table-odd-bg", settings.tableOddColor);
  root.style.setProperty("--plan-table-selected-bg", settings.tableSelectedColor);
  root.style.setProperty(
    "--plan-table-font-family",
    settings.tableFontName || '"Segoe UI", Tahoma, sans-serif'
  );
  root.style.setProperty(
    "--plan-table-font-size",
    settings.tableFontSize > 0 ? `${settings.tableFontSize}px` : "inherit"
  );

  return settings;
}

export function saveWorkplaceSettings(value) {
  const settings = normalizeWorkplaceSettings(value);
  window.localStorage.setItem(WORKPLACE_SETTINGS_KEY, JSON.stringify(settings));
  applyWorkplaceSettings(settings);
  window.dispatchEvent(new CustomEvent(WORKPLACE_SETTINGS_EVENT, { detail: settings }));
  return settings;
}

export function resetTableAppearance(value = loadWorkplaceSettings()) {
  return saveWorkplaceSettings({ ...value, ...DEFAULT_TABLE_APPEARANCE });
}

export function initializeWorkplaceSettings() {
  const settings = applyWorkplaceSettings();

  if (!listenersInstalled) {
    window.addEventListener(WORKPLACE_SETTINGS_EVENT, (event) => {
      applyWorkplaceSettings(event.detail);
    });
    window.addEventListener("storage", (event) => {
      if (event.key === WORKPLACE_SETTINGS_KEY) applyWorkplaceSettings();
    });
    listenersInstalled = true;
  }

  return settings;
}
